import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { AuthSessionService } from './core/auth-session.service';
import { CognitoLogin } from './core/cognito-login';
import { FinlexApiService } from './core/finlex-api.service';
import { FinlexDatasetInfo } from './core/finlex.models';
import { environment } from '../environments/environment';
import { UserMenuComponent } from './features/user-menu/user-menu.component';
import { SiteFooterComponent } from './features/site-footer/site-footer.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    NgOptimizedImage,
    UserMenuComponent,
    SiteFooterComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly finlexApi = inject(FinlexApiService);
  private readonly authSession = inject(AuthSessionService);
  private readonly cognitoLogin = inject(CognitoLogin);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly header = viewChild.required<ElementRef<HTMLElement>>('appHeader');

  protected readonly datasets = signal<FinlexDatasetInfo[]>([]);
  protected readonly datasetError = signal<string | null>(null);
  protected readonly sourceLabel = signal('Finlex-peilipalvelu');
  protected readonly lastSuccessfulSyncAt = signal<string | null>(null);
  protected readonly availableDatasetCount = computed(
    () => this.datasets().filter((dataset) => dataset.available).length,
  );
  protected readonly authState = this.authSession.state;
  protected readonly authIsAdmin = this.authSession.isAdmin;
  protected readonly rakennustietoUrl = environment.rakennustietoUrl;
  protected readonly omaEtusivuUrl = new URL(
    'session/etusivu',
    environment.rakennustietoAccountUrl,
  ).toString();
  private readonly currentUrl = signal(this.router.url);
  protected readonly loginBusy = this.cognitoLogin.busy;

  constructor() {
    afterNextRender(() => this.trackHeaderHeight());
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        queueMicrotask(() => this.document.getElementById('main-content')?.focus());
      });
    this.finlexApi.getDatasets().subscribe({
      next: (response) => {
        this.sourceLabel.set(localizeSourceLabel(response.sourceLabel));
        this.lastSuccessfulSyncAt.set(response.lastSuccessfulSyncAt);
        this.datasets.set(response.datasets);
        this.datasetError.set(null);
      },
      error: () => {
        this.datasetError.set('Finlex-rajapinta ei ole käytettävissä.');
      },
    });
  }

  /** Sticky page content and anchor scrolling stay clear of the sticky header as it wraps. */
  private trackHeaderHeight(): void {
    const header = this.header().nativeElement;
    const root = this.document.documentElement;
    const update = () =>
      root.style.setProperty('--app-header-height', `${header.getBoundingClientRect().height}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected login(): void {
    this.cognitoLogin.start(this.currentUrl());
  }

  protected freshnessLabel(): string {
    const value = this.lastSuccessfulSyncAt();
    return value
      ? `Päivitetty viimeksi ${new Date(value).toLocaleString('fi-FI')}`
      : 'Päivitystä odotetaan';
  }
}

function localizeSourceLabel(sourceLabel: string): string {
  switch (sourceLabel) {
    case 'Finlex API mirror':
    case 'Persistent Finlex backend':
      return 'Finlex-rajapinnan peilipalvelu';
    case 'Local Finlex filesystem fallback, February 2026 snapshot':
      return 'Paikallinen Finlex-varapalvelu, helmikuun 2026 tilannekuva';
    default:
      return sourceLabel;
  }
}
